// mapImportApi — typed fetchers for the floor-plan import pipeline.
//
// Backend: apps/location-api/src/map-imports.ts via the gateway. See
// docs/specs/floor-plan-import.md for the lifecycle + error taxonomy.

import { apiFetch, API_BASE } from "../../lib/api-client.js";
import {
  MAP_IMPORT_STATUSES,
  type MapImportStatus,
  type MapImportErrorCode,
} from "@nexus/shared/map-import";

export type { MapImportStatus, MapImportErrorCode };
export { MAP_IMPORT_STATUSES };

export interface MapImportJobDto {
  id: string;
  tenant_id: string;
  site_id: string | null;
  building_id: string | null;
  floor_id: string | null;
  status: MapImportStatus;
  progress_percent: number;
  current_step: string | null;
  current_message: string | null;
  error_code: MapImportErrorCode | null;
  error_message: string | null;
  warning_messages: string[];
  has_source: boolean;
  has_cleaned: boolean;
  has_svg: boolean;
  has_scene_graph: boolean;
  created_by_user_id: string | null;
  created_at: number;
  updated_at: number;
  completed_at: number | null;
}

export class MapImportApiError extends Error {
  status: number; code: string; correlationId: string | null;
  constructor(status: number, code: string, correlationId: string | null) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId;
  }
}

async function asError(r: Response): Promise<MapImportApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id");
  try {
    const b = (await r.json()) as { error?: string; error_code?: string; correlation_id?: string };
    code = b.error_code ?? b.error ?? code; cid = b.correlation_id ?? cid;
  } catch { /* keep defaults */ }
  return new MapImportApiError(r.status, code, cid);
}

export interface CreateMapImportProgress {
  bytesLoaded: number;
  bytesTotal: number;
  /** 0..1; 0 if `bytesTotal` is not yet known. */
  bytesPercent: number;
}

/**
 * Create a map-import job + upload the source image in a single multipart
 * POST. When `onProgress` is provided we route through XHR so the byte-
 * level upload progress is observable (FloorLayoutEditor uses this to
 * drive its progress bar before redirecting to the progress page).
 * Without `onProgress` we fall back to fetch via `apiFetch` so callers
 * get JWT refresh / correlation-id handling for free.
 */
export async function createMapImport(input: {
  file: File;
  site_id?: string | null;
  building_id?: string | null;
  floor_id?: string | null;
  /**
   * super_admin impersonating into another tenant (demo / support flow)
   * must pass `?tenant_id=` so Rbac.resolveTenant on the backend can
   * route the request. Regular tenant users leave this null.
   */
  tenant_id?: string | null;
  onProgress?: (p: CreateMapImportProgress) => void;
  signal?: AbortSignal;
}): Promise<{ ok: true; job: MapImportJobDto }> {
  const fd = new FormData();
  fd.append("file", input.file);
  if (input.site_id) fd.append("site_id", input.site_id);
  if (input.building_id) fd.append("building_id", input.building_id);
  if (input.floor_id) fd.append("floor_id", input.floor_id);
  const qs = input.tenant_id ? `?tenant_id=${encodeURIComponent(input.tenant_id)}` : "";

  if (!input.onProgress && !input.signal) {
    const r = await apiFetch(`/v1/map-imports${qs}`, { method: "POST", body: fd, credentials: "include" });
    if (!r.ok) throw await asError(r);
    return (await r.json()) as { ok: true; job: MapImportJobDto };
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}/v1/map-imports${qs}`, true);
    xhr.withCredentials = true;
    xhr.responseType = "text";

    if (input.signal) {
      if (input.signal.aborted) {
        reject(new MapImportApiError(0, "aborted", null));
        return;
      }
      input.signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    let total = 0;
    xhr.upload.addEventListener("progress", (e) => {
      total = e.lengthComputable ? e.total : total;
      const loaded = e.loaded;
      input.onProgress?.({
        bytesLoaded: loaded,
        bytesTotal: total,
        bytesPercent: total > 0 ? Math.min(loaded / total, 1) : 0,
      });
    });
    xhr.addEventListener("load", () => {
      const cid = xhr.getResponseHeader("x-correlation-id");
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText) as { ok: true; job: MapImportJobDto }); }
        catch { reject(new MapImportApiError(xhr.status, "invalid_json", cid)); }
        return;
      }
      let code = "request_failed";
      try {
        const body = JSON.parse(xhr.responseText) as { error?: string; error_code?: string };
        code = body.error_code ?? body.error ?? code;
      } catch { /* keep default */ }
      reject(new MapImportApiError(xhr.status, code, cid));
    });
    xhr.addEventListener("error", () => {
      reject(new MapImportApiError(0, "network", xhr.getResponseHeader("x-correlation-id")));
    });
    xhr.addEventListener("abort", () => {
      reject(new MapImportApiError(0, "aborted", xhr.getResponseHeader("x-correlation-id")));
    });
    xhr.send(fd);
  });
}

/** Build `?tenant_id=...` (or empty) — used by every map-import call so
 *  super_admin impersonation (demo / support) routes through Rbac.resolveTenant
 *  on the worker side. */
function tenantQuery(tenantId: string | null | undefined): string {
  return tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
}

export async function getMapImport(id: string, tenantId: string | null = null): Promise<MapImportJobDto> {
  const r = await apiFetch(`/v1/map-imports/${encodeURIComponent(id)}${tenantQuery(tenantId)}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  const body = (await r.json()) as { job: MapImportJobDto };
  return body.job;
}

export async function listMapImports(params: {
  status?: MapImportStatus[];
  floor_id?: string;
  limit?: number;
  tenant_id?: string | null;
} = {}): Promise<MapImportJobDto[]> {
  const qs = new URLSearchParams();
  if (params.status?.length) qs.set("status", params.status.join(","));
  if (params.floor_id) qs.set("floor_id", params.floor_id);
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.tenant_id) qs.set("tenant_id", params.tenant_id);
  const r = await apiFetch(`/v1/map-imports${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return ((await r.json()) as { items: MapImportJobDto[] }).items;
}

export async function retryMapImport(id: string, tenantId: string | null = null): Promise<void> {
  const r = await apiFetch(`/v1/map-imports/${encodeURIComponent(id)}/retry${tenantQuery(tenantId)}`, { method: "POST", credentials: "include" });
  if (!r.ok) throw await asError(r);
}

export async function cancelMapImport(id: string, tenantId: string | null = null): Promise<void> {
  const r = await apiFetch(`/v1/map-imports/${encodeURIComponent(id)}/cancel${tenantQuery(tenantId)}`, { method: "POST", credentials: "include" });
  if (!r.ok) throw await asError(r);
}

export interface ApproveMapImportResult {
  ok: true;
  layout_id: string;
  layout_version_id: string;
  version_no: number;
  floor_id: string;
  site_id: string | null;
  building_id: string | null;
  markers_carried_over: number;
}

export async function approveMapImport(id: string, tenantId: string | null = null): Promise<ApproveMapImportResult> {
  const r = await apiFetch(`/v1/map-imports/${encodeURIComponent(id)}/approve${tenantQuery(tenantId)}`, { method: "POST", credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as ApproveMapImportResult;
}

/** Build the URL the browser uses to display an artifact (source / svg
 *  preview). Streaming is server-side; we return a cookie-authenticated
 *  URL the <img> tag can load directly. `cleaned-meta` returns the bbox
 *  JSON produced by the furniture-cleanup stage; the page uses it as
 *  the input to canvas-side cleaned-image synthesis. */
export type MapImportArtifactKind = "source" | "cleaned" | "svg" | "scene-graph" | "cleaned-meta";

export function artifactUrl(jobId: string, kind: MapImportArtifactKind, tenantId: string | null = null): string {
  return `${API_BASE}/v1/map-imports/${encodeURIComponent(jobId)}/artifacts/${kind}${tenantQuery(tenantId)}`;
}

// ── Phase 2: cleaned-image canvas-synthesis flow ──────────────────────

/** Shape of the cleaned-meta.json artifact written by the map-import
 *  pipeline's furniture_cleanup stage. Bbox coordinates are in source
 *  image pixel space (origin top-left). Frontend canvas synthesis MUST
 *  clamp bbox coordinates to image bounds before drawing — the model
 *  occasionally emits 1-2 px overflows. */
export interface CleanedMetaBBox {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** FurnitureBBoxKind from the backend; loosened to string here to
   *  avoid cross-package coupling. Frontend doesn't branch on kind for
   *  the canvas synth — every bbox gets the same white rectangle
   *  treatment. */
  kind: string;
  confidence: number;
}

export interface CleanedMetaArtifact {
  schema_version: 1;
  job_id: string;
  tenant_id: string;
  generated_at: string;
  model: string;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    cost_usd: number;
    model: string;
  };
  image_size: { width: number; height: number };
  bboxes: CleanedMetaBBox[];
}

/** Fetch the cleaned-meta.json artifact. The page calls this once the
 *  job reaches `ready_for_review` and `has_cleaned` is still false,
 *  feeding the result into the canvas-synthesis effect. */
export async function fetchCleanedMeta(id: string, tenantId: string | null = null): Promise<CleanedMetaArtifact> {
  const r = await apiFetch(
    `/v1/map-imports/${encodeURIComponent(id)}/artifacts/cleaned-meta${tenantQuery(tenantId)}`,
    { credentials: "include" },
  );
  if (!r.ok) throw await asError(r);
  return (await r.json()) as CleanedMetaArtifact;
}

/** Upload the canvas-synthesised cleaned PNG. Server PUTs it to R2 and
 *  stamps `cleaned_image_r2_key` on the row, so subsequent polls return
 *  `has_cleaned: true` and the cleaned tab unlocks. Idempotent —
 *  re-uploading overwrites the R2 object and writes a fresh audit_log
 *  entry (intentional, so reviewers see the upload history). */
export async function uploadCleanedImage(id: string, png: Blob, tenantId: string | null = null): Promise<{ ok: true; r2_key: string }> {
  const fd = new FormData();
  fd.append("file", png, "cleaned.png");
  const r = await apiFetch(`/v1/map-imports/${encodeURIComponent(id)}/cleaned-image${tenantQuery(tenantId)}`, {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { ok: true; r2_key: string };
}

/** Translate an error_code into a zh-TW message for the failure UI. */
export function friendlyMapImportError(code: MapImportErrorCode | null, fallbackMessage?: string | null): string {
  if (!code) return fallbackMessage ?? "轉換失敗，請重試。";
  switch (code) {
    case "IMAGE_TOO_LOW_RESOLUTION":
      return "圖片解析度過低，建議重新上傳較清晰的平面圖（建議 1500×1500 以上）。";
    case "IMAGE_TOO_BLURRY":
      return "圖面過於模糊。請使用清晰的平面圖原檔，避免拍照或螢幕翻拍。";
    case "UNSUPPORTED_FILE_TYPE":
      return "不支援的檔案格式。可使用 PNG、JPEG、WebP 或 PDF。";
    case "PREPROCESSING_FAILED":
      return "前處理階段失敗，請重試。";
    case "FURNITURE_CLEANUP_FAILED":
      return "家具清除階段失敗。圖面可能雜訊過多，請先裁切或換一張較乾淨的圖。";
    case "STRUCTURE_EXTRACTION_FAILED":
      return "結構抽取失敗（AI 模型回應異常）。請重試；多次失敗請聯絡管理員。";
    case "VECTORIZE_FAILED":
      return "向量化失敗，請重試。";
    case "SCENE_GRAPH_FAILED":
      return "場景圖產生失敗，請重試。";
    case "STORAGE_FAILED":
      return "雲端儲存錯誤，請稍候再試。";
    case "JOB_TIMEOUT":
      return "轉換時間過長（超過 5 分鐘）。請改用較小的圖檔或聯絡管理員。";
    case "PERMISSION_DENIED":
      return "權限不足，無法執行此操作。";
  }
}
